import { useQuery, QueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

/**
 * Finds queries whose keys start with "cachedObjectUrls",
 * revokes the associated object URLs, and removes those queries from the cache.
 *
 * @param queryClient - The React Query client instance.
 */
export const revokeCachedObjectUrls = (queryClient: QueryClient) => {
  // Find all queries with keys starting with 'cachedObjectUrls'
  const queries = queryClient.getQueryCache().findAll({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey[0] === 'cachedObjectUrls',
  });

  queries.forEach((query) => {
    const objectUrl = query.state.data;
    if (objectUrl && typeof objectUrl === 'string') {
      URL.revokeObjectURL(objectUrl);
    }
    // Remove the query from the cache
    queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
  });
};

export type UseObjectUrlProps = {
  /** A unique key that will be part of the React Query key */
  key: string;
  blob?: Blob | null;
};

/**
 * Creates an object URL based on a blob using React Query for an additional
 * caching layer. This is useful to employ in virtualization applications where
 * you want to minimize reloading when image components mount and unmount
 * frequently.
 *
 * When the blob changes, a new object URL is created (using the provided key as part
 * of the React Query key), and the previous URL is revoked.
 *
 * NOTE: We do not do any memory cleanup here on unmount for performance
 * optimization with virtualized components. Ensure when you use this, you
 * call revokeCachedObjectUrls defined in this file when you unmount the context
 * where this is being used.
 */
export const useMemoryUnsafeCachedObjectUrl = ({
  key,
  blob,
}: UseObjectUrlProps) => {
  // Combine the unique key and blob to form the query key.
  const queryKey = ['cachedObjectUrls', key, blob];

  // Ref to store the previous object URL so we can revoke it when a new one is generated.
  const previousUrlRef = useRef<string | null>(null);

  const {
    data: objectUrl,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (blob) {
        const nextUrl = URL.createObjectURL(blob);
        if (previousUrlRef.current && previousUrlRef.current !== nextUrl) {
          URL.revokeObjectURL(previousUrlRef.current);
        }
        previousUrlRef.current = nextUrl;
        return nextUrl;
      } else {
        return null;
      }
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return {
    objectUrl,
    isLoading,
    isError,
    error,
  };
};
